/**
 * ParallaxWithFadeout - A lightweight parallax scrolling library with optional fade effects
 *
 * Creates smooth parallax background movement based on scroll position with optional overlay fadeout.
 * Supports multiple movement directions and adjustable distances for customizable parallax effects.
 *
 * @param {HTMLElement} element - The DOM element to apply parallax effect to
 * @param {Object} options - Configuration options
 * @param {string} [options.direction='fixed'] - Parallax direction: 'up', 'down', 'left', 'right', or 'fixed'
 * @param {number} [options.distance=150] - Maximum parallax movement distance in pixels
 * @param {number} [options.headerOffset=0] - Vertical offset to account for fixed headers
 * @param {boolean} [options.fade=false] - Enable overlay fade effect on scroll
 */

import { isMobileDynamic } from "../../../assets/scripts/utils/isMobileDynamic";

export default class ParallaxWithFadeout {
  constructor(element, options = {}) {
    this.element = element;
    this.direction = options.direction || "fixed"; // up, down, left, right, fixed
    this.distance = options.distance || 150;
    this.headerOffset = options.headerOffset || 0; // Offset for fixed headers
    this.fade = options.fade || false;
    this.listener = null;
    this.section = this.element.closest(".comp-section") || null;
    this.overlay = this.section?.querySelector(".overlay") || null;
    this.hasParallax = options.hasParallax || false;
    this.opacityApplied = options.opacityApplied || false;
    this.init();
  }

  init() {
    // Set up scroll listener
    this.setupScrollListener();
  }

  destroy() {
    if (this.listener) {
      window.removeEventListener("scroll", this.listener);
      this.listener = null;
    }
  }

  setupScrollListener() {
    let ticking = false;

    const updateTransform = () => {
      const rect = this.element.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      // Calculate progress (0 to 1)
      // When element bottom hits viewport top: progress = 0
      // When element top hits viewport bottom: progress = 1
      const progress = Math.max(
        0,
        Math.min(1, (windowHeight - rect.top) / (windowHeight + rect.height)),
      );

      if (this.hasParallax) {
        // Interpolate between start and end values
        const transform = this.interpolateTransform(progress);

        // Apply transform
        this.element.style.backgroundPosition = transform;
      }

      // Apply fadeout
      this.calculateFadeOut(progress);

      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(updateTransform);
        ticking = true;
      }
    };

    this.listener = onScroll;

    window.addEventListener("scroll", this.listener, {
      passive: true,
    });

    // Initial calculation
    updateTransform();
  }

  interpolateTransform(progress) {
    const { direction, distance, headerOffset } = this;
    const offset = (progress - 0.5) * distance;

    switch (direction) {
      case "up":
        return `50% calc(50% - ${offset}px)`;
      case "down":
        return `50% calc(50% - ${-offset}px)`;
      case "left":
        return `calc(50% - ${offset}px) calc(50% + ${headerOffset}px)`;
      case "right":
        return `calc(50% - ${-offset}px) calc(50% + ${headerOffset}px)`;
      default:
        return "";
    }
  }

  calculateFadeOut(progress) {
    const isMobile = isMobileDynamic();

    if (this.overlay) {
      if (this.fade && !isMobile) {
        this.overlay.style.opacity = progress;
      } else if (isMobile && !this.opacityApplied) {
        this.overlay.style.opacity = 0;
      }
    }
  }
}
